import { CustomerModelType } from '@teinor/erp/types/company/customer'
import { customerTypes, CustomerTypesInversedKeys } from '@teinor/erp/types/company/customer/customerTypes'
import { useLocation } from 'react-router'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { TwinTableLink } from '../../../../../../baseComponents/TwinTable'
import { BaseScreenTableSimpleStateless, useBaseScreenTableLogic } from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { EditRow, CustomRenderString, DeleteRow, CustomRenderBoolean } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import ModalCreateContactB2C from './ModalCreateContactB2C'
import { EmployeesPayload } from '../../../../../../utils/reducers/company/employees'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import withLoading from '../../../../../../utils/hoc/withLoading'
import { getAllClientManagerEmployees, getConfigParam } from '../../../../../../utils/reducers/getters'
import { useMemo } from 'react'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import useEditDeleteModal from '../../../../../../utils/hooks/useEditDeleteModal'
import ModalChangeActiveCustomer from '../../../../../../specificComponents/Customers/ModalChangeActiveCustomerCustomer'
import { faRotateLeft } from '@fortawesome/pro-light-svg-icons'
import useUpdateTableInstance from '../../../../../../utils/hooks/useUpdateTableInstance'
import RightHeaderTableCustomers from '../../../../../../specificComponents/Customers/RightHeaderTableCustomers'

interface ContactsB2CProps extends ComponentWithPermissions { }

const ContactsB2C: React.FC<ContactsB2CProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal, tableInstance, setTableInstance } = useBaseScreenTableLogic()
    const { updateTableInstance } = useUpdateTableInstance({ tableInstance })
    const pageData = {
        title: t('leadsLong', 'Clientes Potenciales'),
        newText: t('newLead', 'Nuevo Cliente Potencial'),
    }

    return (
        <BaseScreenTableSimpleStateless TableComponent={TableContactsB2CConnect} {...{ pageData, userPermissions, updateTableInstance }} CreateModalComponent={ModalCreateContactB2C} logicProps={{ openModal, setOpenModal, tableInstance, setTableInstance }} RightHeader={
        <RightHeaderTableCustomers typeCustomer={customerTypes['contactB2C'] as CustomerTypesInversedKeys}  {...{ pageData, openModal, setOpenModal, userPermissions, updateTableInstance }} />}/>
    )
}

type TableContactsB2CProps = TableComponentProps & ReduxTableContactsB2C  & {}

const TableContactsB2C: React.FC<TableContactsB2CProps> = ({ setTableInstance, employees, userPermissions, tableInstance }) => {
    const { t } = useTwinTranslation()
    const location = useLocation().pathname
    const myEmployees = useMemo(() => getConfigParam('showOnlyClientManagerEmployees') === 'on' ? getAllClientManagerEmployees() || {} : employees || {}, [employees])
    const { openModal, setOpenModal } = useEditDeleteModal()

    const columns: ColumnTableSchema<CustomerModelType> = [
        {
            id: 'code',
            dataKey: 'code',
            label: t('code', 'Código'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('customerCode', 'Código de cliente') }
            },
            searchKey: 'Customer.code',
            sortable: true
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            searchKey: 'Customer.name'
        },
        {
            id: 'surname',
            dataKey: 'surname',
            label: t('surname', 'Apellido'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('surname', 'Apellido') }
            },
            searchKey: 'Customer.surname'
        },
        {
            id: 'email',
            dataKey: 'email',
            label: t('email', 'Email'),
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            searchKey: 'Customer.email'
        },
        {
            id: 'phone',
            dataKey: 'phone',
            label: t('phone', 'Teléfono'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('phone', 'Teléfono') }
            },
            searchKey: 'Customer.phone'
        },
        {
            id: 'mobile',
            dataKey: 'mobile',
            label: t('mobile', 'Móvil'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('mobile', 'Móvil') }
            },
            searchKey: 'Customer.mobile'
        },
        {
            id: 'CustomerOriginId',
            dataKey: 'CustomerOriginId',
            label: t('origin', 'Origen'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerOrigin?.name} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('origin', 'Origen') }
            },
            searchKey: 'CustomerOrigin.name',
            hideColumnByDefault: true
        },
        {
            id: 'ResponsibleEmployeeId',
            dataKey: 'ResponsibleEmployeeId',
            label: t('ourManager', 'Nuestro gestor'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.ResponsibleEmployee?.fullname_short} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: myEmployees, label: t('ourManager', 'Nuestro gestor'), fieldName:'fullname_short' }
            },
            sortable: true,
            searchSingleKey: 'ResponsibleEmployeeId',
            searchKey: 'fullname_short',
            hideColumnByDefault: true
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabled', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            searchKey: 'Customer.active',
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpenModal({ type: 'delete', allRowData })} icon={!allRowData?.active ? faRotateLeft : undefined} messageTooltip={!allRowData?.active ? t('reactivate', 'Reactivar') : undefined} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]
    return (
        <TwinTableLink createRowDataLink={[location, ['$id', 'main']]} name='contactsB2C' columns={columns} getDataFrom='/api/app/customer/getAllCustomers' extraWhereParams={{ 'customer_type': customerTypes['contactB2C']}} setTableInstance={setTableInstance}>
            {openModal?.type === 'delete' ? <ModalChangeActiveCustomer {...{ setOpened: () => setOpenModal(null) }} allRowData={openModal.allRowData} onSubmit={() => tableInstance?.getTableDataFromStart()} /> : null}
        </TwinTableLink>
    )
}
const tableContactsB2CDispatch = {
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

type ReduxTableContactsB2C = ConnectedProps<typeof tableContactsB2CConnect>
const mapTableContactsB2CConnector = (state: AllReduxPayloads) => ({ employees: state.employees })
const tableContactsB2CConnect = connect(mapTableContactsB2CConnector, tableContactsB2CDispatch)
const tableContactsB2CLoading = withLoading(TableContactsB2C, [{ fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])
const TableContactsB2CConnect = tableContactsB2CConnect(tableContactsB2CLoading)
export default ContactsB2C